// Package imports:
import React, { useState } from 'react';
// Component imports:
import DisplayBox from '../DisplayBox/DisplayBox';
import Link from '../../ui-elements/Link/Link';
import Label from '../../ui-elements/Label/Label';
import Fyrirvari from '../Fyrirvari/Fyrirvari';
import LmdTable from '../../ui-elements/Table/LmdTable';
// Service imports:
import { formatNumber, getTableDate } from '../../services/utils';
import { useApiLmdData } from '../../services/apiHooks';
// Type imports:
import { IDefaultProps } from '../../types/Types';
import { IApiLmdCompanyReportsAnalysis } from '../../types/HlutabrefTypes';

const getSymbolFromName = (name: string) => {
    switch (name) {
        case 'Hagar':
            return 'HAGA';
        case 'Sýn':
            return 'SYN';
        case 'VÍS':
            return 'VIS';
        case 'Eimskip':
            return 'EIM';
        case 'Skeljungur':
            return 'SKEL';
        case 'Siminn':
            return 'SIMINN';
        case 'Íslandsbanki':
            return 'ISB';
        default:
            return name.toUpperCase();
    }
}

interface IOwnProps {
    symbol?: string,
    limit?: number
}

type Props = IOwnProps & IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

const CompanyReportsAnalysis: React.FC<Props> = ({
    symbol,
    limit = 15,
    accessToken,
    refreshRateMs,
    ...props
}) => {
    const [data] = useApiLmdData<IApiLmdCompanyReportsAnalysis[]>(
        `/v1/market_data/v1/company_reports/type/valuation/analyst/*/reference/${symbol ?? '*'}/category/*/`,
        accessToken,
        refreshRateMs
    );
    const [disclaimerOpen, setDisclaimerOpen] = useState(false);
    const openDisclaimer = () => setDisclaimerOpen(true);
    const closeDisclaimer = () => setDisclaimerOpen(false);

    const displayData = data.data === null
        	? null
            : data.data.sort((a, b) => {
                const aval = a.DateOfAnalysis;
                const bval = b.DateOfAnalysis;
                return aval === null && bval === null
                    ? 0
                    : aval === null
                    ? 1
                    : bval === null
                    ? -1
                    : aval > bval
                    ? -1
                    : aval < bval
                    ? 1
                    : 0;
            }).slice(0, limit);

    return (
        <DisplayBox
            className='KCL_company-reports-analysis'
            title="Greiningar"
            {...props}
        >
            <LmdTable
                apiData={{...data, data: displayData}}
                columns={symbol ? [{
                    title: 'Greinandi',
                    dataAccessor: ({Website, AnalystName}) => <Link url={Website ?? ''} targetBlank linkSize='15'>{AnalystName}</Link>,
                    textAlign: 'left'
                }, {
                    title: 'Dags.',
                    dataAccessor: ({DateOfAnalysis}) => DateOfAnalysis && getTableDate(DateOfAnalysis.toString(), 'DD/MM/YYYY', '.'),
                    textAlign: 'left'
                }, {
                    title: 'Lýsing',
                    dataAccessor: ({ShortDesc}) => ShortDesc,
                    textAlign: 'left'
                }, {
                    title: 'Verðmatsgengi',
                    dataAccessor: ({Value}) => formatNumber(Value, null, 2)
                }] : [{
                    title: 'Auðkenni',
                    dataAccessor: ({ReferenceSymbol}) => <Link url={`/Markadir/Hlutabref/${ReferenceSymbol !== null ? getSymbolFromName(ReferenceSymbol) : ''}`} linkSize='15'>{ReferenceSymbol}</Link>,
                    textAlign: 'left'  
                }, {
                    title: 'Greinandi',
                    dataAccessor: ({Website, AnalystName}) => <Link url={Website ?? ''} targetBlank linkSize='15'>{AnalystName}</Link>,
                    textAlign: 'left'
                }, {
                    title: 'Dags.',
                    dataAccessor: ({DateOfAnalysis}) => DateOfAnalysis && getTableDate(DateOfAnalysis.toString(), 'DD/MM/YYYY', '.'),
                    textAlign: 'left'
                }, {
                    title: 'Lýsing',
                    dataAccessor: ({ShortDesc}) => ShortDesc,
                    textAlign: 'left'
                }, {
                    title: 'Verðmatsgengi',
                    dataAccessor: ({Value}) => formatNumber(Value, null, 2)
                }]}
                tableSize="lg"
            />
            <span className="label-wrapper" onClick={openDisclaimer}>
                <Label text="Fyrirvari" labelType="disclaimer" />
            </span>
            <Fyrirvari show={disclaimerOpen} close={closeDisclaimer}>
                <p>Þær skoðanir og spár sem hér birtast eru byggðar á almennum upplýsingum sem Jakobsson Capital ehf. hefur undir höndum þegar greiningin er gerð. Helstu heimildir eru upplýsingar frá Kauphöllinni og vefsíðum fyrirtækja í Kauphöllinni, Reuters fréttaveitunni, auk annarra opinberra upplýsinga. Upplýsingar í greiningu þessari eru settar fram samkvæmt bestu vitund greinenda hverju sinni en þær eru háðar breytingum sem geta haft áhrif á greininguna. Jakobsson Capital ehf. ábyrgst ekki áreiðanleika eða réttmæti upplýsinga sem settar eru fram í þessari greiningu.</p>
                <p>Greining eða verðmatsgengi felur ekki í sér persónulega fjárfestingarráðgjöf í skilningi laga nr. 108/2007 né ráðleggingar af neinu tagi. Greiningunni eða verðmatsgenginu er ætlað að vera til upplýsingar og engin trygging eða ábyrgð er veitt á því að nokkrar af þeim spám eða fyrirætlunum sem lýst er í greiningu gangi eftir. Framtíðarspár og fyrirætlanir eru ávallt háðar ýmsum áhættum og óvissuþáttum sem gætu leitt til verulega breyttra niðurstaðna. Jakobsson Capital ehf. vinnur greininguna sem verðmatsgengið er byggt á án skuldbindingar um að leggja fram frekari upplýsingar, uppfæra eða leiðrétta ónákvæmni sem síðar kann að koma í ljós.</p>
                <p>Jakobsson Capital ehf. eða tengdir aðilar eru með engum hætti ábyrgir fyrir hvers konar tjóni eða tapi, af hvaða tagi sem er, beint eða óbeint, sem fjárfestar og/eða hverjir þeir sem kynna sér greininguna kunna að verða fyrir með því að notast við eða nýta sér með öðrum hætti greiningu þessa eða innihald hennar. Jakobsson Capital ehf., starfsmenn. stjórnarmenn og/eða aðilar tengdir Jakobsson Capital ehf. kunna að eiga hagsmuna að gæta varðandi einstök verðbréf sem greiningar, verðmatsskýrslur og annað útgáfuefni Jakobsson Capital ehf. lýtur að hverju sinni.</p>
            </Fyrirvari>
        </DisplayBox>
    );
}

export default CompanyReportsAnalysis;